/* eslint-disable no-undef */
/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */

import {
  getUserData as GBC_getUserData,
  setUserData as GBC_setUserData,
} from '@sgorg/game-backend-client';

import { OMT_Utils } from './utils/OMT_Utils';
import { OMT_Crypt } from './OMT_Crypt';
import { OMT_LegacyCC2DataHandler } from './OMT_LegacyCC2DataHandler';

const OMT_STORAGE_KEYS = ['gmdatastring-cc2', 'offline-scores', 'DDNA-data'];

/**
 * class for interfacing with and storing saved user data
 */
export class OMT_UserData {
  /**
   * constructor
   */
  constructor() {
    this._gbUserData = null;
    this._gbDataSynced = false;
    this._writeDataQueue = [];

    this._lsUserData = null;
    this._lsWriteInProgress = false;
    this._lsWriteQueue = [];

    // for CC2 data conversion
    this._cc2DataWasImported = false;
    this._cc2LegacyDataHandler = new OMT_LegacyCC2DataHandler();
  }

  /**
   * initialize and load user data
   */
  async init() {
    // load user data from game-backend
    try {
      this._gbUserData = await GBC_getUserData();
      this._gbDataSynced = true;
      OMT_Utils.stylizedLog('OMT_UserData: UserData recieved');
    } catch (e) {
      this._gbUserData = null;
      this._gbDataSynced = false;
      console.error('OMT_UserData: Could not load user data from GB.');
    }

    // this is CC2 so we must load FB data as well.
    if (this.isCC2Data) {
      try {
        await this._cc2LegacyDataHandler.loadLegacyCC2Data();
      } catch (e) {
        console.error('OMT_UserData: Could not load user data from FB.');
      }
    }
  }

  /**
   * check if we are playign cookie crush
   * @returns {boolean}
   */
  get isCC2Data() {
    return G.BuildEnvironment.APP_ID === 'cookie-crush-2';
  }

  /**
   * true if we imported CC2 data this session
   * @returns {boolean}
   */
  get cc2DataWasImported() {
    return this.isCC2Data && this._cc2DataWasImported;
  }

  /**
   * get user data once loaded
   * @param {string} dataKey data key for storage
   * @returns {Object}
   */
  getUserData(dataKey) {
    let data;
    // we need convert data if old CC2 data
    if (this.isCC2Data && dataKey === G.saveState.dataKey && this._gbUserData != null && this._cc2LegacyDataHandler.isLegacyCC2ImportRequired(this._gbUserData)) {
      data = this._cc2LegacyDataHandler.convertCC2toOMTData(this._gbUserData);
      this._gbUserData[dataKey] = data;
      this._cc2DataWasImported = true;
    } else { // data is in OMT format
      data = this._gbUserData ? this._gbUserData[dataKey] : null;
    }
    // parse JSON if neccessary
    if (data && JSONUtil.isJson(data)) data = JSON.parse(data);

    // if data was just imported from CC2 we dont want to check fallbacks
    if (!this.cc2DataWasImported) {
      // get fallback data
      const fallbackData = this._readFromLocalStorage(dataKey);
      // check if fallback data is newer then the server loaded data and save it to server if so
      if (data && fallbackData && this._useFallbackData(dataKey, data, fallbackData)) {
        OMT_Utils.stylizedLog(`OMT_UserData: [${dataKey}]  Local data is prefered. Fallback data used`, '#FFFF00');
        this.writeUserData(dataKey, fallbackData);
        return fallbackData;
      }
      // use fallback data if no data
      if (!data && fallbackData) {
        OMT_Utils.stylizedLog(`OMT_UserData: [${dataKey}] No data received. Fallback data used`, '#FFFF00');
        return fallbackData;
      }
    }
    return data;
  }

  /**
   * Check if we should use fallback / local storage data
   * @param {string} dataKey
   * @param {Object} data
   * @param {Object} fallbackData
   * @returns {boolean} true if fallout data is newer
   */
  _useFallbackData(dataKey, data, fallbackData) {
    const hasTimestamps = data.lastChange != null && fallbackData.lastChange != null;
    const fallbackIsNewer = hasTimestamps && fallbackData.lastChange > data.lastChange;

    // conditions for the G.saveState data key
    if (dataKey === G.saveState.dataKey) {
      // compare IAP counts
      const IAPCount = data.IAPCount ? data.IAPCount : 0;
      const fallbackIAPCount = fallbackData.IAPCount ? fallbackData.IAPCount : 0;
      if (fallbackIAPCount !== IAPCount) return fallbackIAPCount > IAPCount;

      // compare levels / stars
      const calculateTotalStars = (dataObj) => {
        const levels = dataObj.levels ? dataObj.levels : [];
        let totalStars = 0;
        for (let i = 0; i < levels.length; i++) totalStars += levels[i] || 0;
        return totalStars + (dataObj.extraStars ? dataObj.extraStars : 0);
      };
      const totalStars = calculateTotalStars(data);
      const fallbackTotalStars = calculateTotalStars(fallbackData);
      if (fallbackTotalStars !== totalStars) return fallbackTotalStars > totalStars;
    }

    // conditions for all data. compare timestamps if they exist.
    return fallbackIsNewer;
  }

  /**
   * write user data.
   * @param {string} key data key for storage
   * @param {object} data data to write to storage
   */
  async writeUserData(key, data) {
    // eslint-disable-next-line no-unreachable
    if (JSONUtil.isJson(data)) data = JSON.parse(data);

    // save data to GB and LS for fallback
    this._writeToLocalStorage(key, data);

    // write to server only if gb data was synced
    // if (this._gbDataSynced) {
    //   const prevQueueItem = this._findQueuedWriteByKey(key);
    //   if (prevQueueItem) { // prev item with matching id exists just swap the data keep the queue order
    //     prevQueueItem.data = data;
    //     // OMT_Utils.stylizedLog(`OMT_UserData: [${key}] key was already in write queue.`, '#FFFF00');
    //   } else { // this key is not currently in the queue so add it.
    //     this._writeDataQueue.push({ key, data });
    //     // if the item just pushed is the only item in the queue being writing
    //     if (this._writeDataQueue.length === 1) {
    //       await this._writeQueuedUserData();
    //     }
    //   }
    // } else {
    //   OMT_Utils.stylizedLog(`OMT_UserData: [${key}] data was written locally only as game-backend was not synced.`, '#FFFF00');
    // }
  }

  /**
   * remove queue entries with matching keys
   * @param {string} key
   * @returns {Object} queue object { key, data }
   */
  _findQueuedWriteByKey(key) {
    const queueItems = this._writeDataQueue.filter((queueItem) => queueItem.key === key);
    if (queueItems.length > 0) return queueItems[0];
    return null;
  }

  /**
   * write queued user data to game-backend in sequence queued
   */
  async _writeQueuedUserData() {
    if (this._writeDataQueue.length === 0) return;
    const { key } = this._writeDataQueue[0];
    const { data } = this._writeDataQueue[0];

    try {
      const writeData = { [key]: data };
      await GBC_setUserData(writeData);
      OMT_Utils.stylizedLog(`OMT_UserData: [${key}] written successfully to the game-backend.`, '#00FF00');
      this._gbUserData[key] = data;
    } catch (error) {
      OMT_Utils.stylizedLog('OMT_UserData: could not write userData to the game-backend.', '#FFFF00');
    }

    // remove from the queue
    this._writeDataQueue.shift();

    // continue the write queue
    if (this._writeDataQueue.length > 0) {
      await this._writeQueuedUserData();
    }
  }

  /**
   * delete user data object from local storage
   * @param {string} dataKey
   * @returns {Object}
   */
  _readFromLocalStorage(dataKey = null) {
    if (this._lsUserData) { // return cached data if already read
      return dataKey == null ? this._lsUserData : this._lsUserData[dataKey];
    }
    // generate AES cryptKey and data storageKey
    const cryptKey = OMT_Crypt.generateLocalStorageCryptKey(G.BuildEnvironment.APP_ID, OMT.envData.settings.user.userId);
    const storageKey = OMT_Crypt.md5Hash(`${G.BuildEnvironment.APP_ID}-${OMT.envData.settings.user.userId}`);

    // decode + parse data
    try {
      //let data = localStorage.getItem(storageKey);
      let data = null;

      window.sdkHandler.trigger('restore', {
        key: storageKey,
        callback:  (error, value) => {
          if (error) {
            console.log("Couldn't restore savedata");
          } else {
              data = value;
          }
        }
      }, this);


      if (data) {
        try { // decode the LS data if it is encrypted with AES
          data = OMT_Crypt.decode(cryptKey, data);
          console.log("decoded data", data);
          OMT_Utils.stylizedLog('OMT_UserData: decoded AES encrypted localStorage data', '#00FF00');
        } catch (error) {
          data = this._decompressLocalStorageData(data);
          OMT_Utils.stylizedLog('OMT_UserData: decoded LZUTF8 compressed localStorage data', '#00FF00');
        }
        data = JSONUtil.isJson(data) ? JSON.parse(data) : data;
        this._lsUserData = data; // store the decoded LS data
      } else {
        return null;
      }
      if (dataKey) data = data[dataKey]; // fetch the requested data key
      return data;
    } catch (error) {
      console.log("error!");
      return null;
    }
  }

  /**
   * write user data to local storage
   * @param {string} dataKey
   */
  async _writeToLocalStorage(dataKey, data) {
    // if a write is in progress queue this write
    if (this._lsWriteInProgress) {
      // if this key is already in queue swap the data
      for (let i = 0; i < this._lsWriteQueue.length; i++) {
        if (this._lsWriteQueue[i].dataKey === dataKey) {
          this._lsWriteQueue[i].data = data;
          return;
        }
      }
      // key is not in the queue add it
      this._lsWriteQueue.push({ dataKey, data });
      return;
    }
    this._lsWriteInProgress = true;

    const storageKey = OMT_Crypt.md5Hash(`${G.BuildEnvironment.APP_ID}-${OMT.envData.settings.user.userId}`);
    let storageData = this._readFromLocalStorage();
    if (!storageData) storageData = {};
    storageData[dataKey] = data;

    try {
      const compressedData = await this._compressLocalStorageData(storageData);
      OMT_Utils.stylizedLog(`OMT_UserData: [${dataKey}] written to local-storage`, '#00FF00');
      //localStorage.setItem(storageKey, compressedData);

      window.sdkHandler.trigger('save', {
        key: storageKey,
        value: compressedData,
        callback: () => { }
      });

    } catch (error) {
      OMT_Utils.stylizedLog('OMT_UserData: could not write userData to the local-storage', '#FFFF00');
    }

    // continue writing if any write requests were made during data encoding
    this._lsWriteInProgress = false;
    if (this._lsWriteQueue.length > 0) {
      // OMT_Utils.stylizedLog('OMT_UserData: a local storage write is in progress, this write will be queued.', '#FFFF00');
      const queuedData = this._lsWriteQueue.shift();
      this._writeToLocalStorage(queuedData.dataKey, queuedData.data);
    }
  }

  /**
   * asyncronously compress data for local storage output
   * @param {Object} data
   * @returns {Promise}
   */
  async _compressLocalStorageData(data) {
    return new Promise((resolve) => {
      LZUTF8.compressAsync(JSON.stringify(data), { outputEncoding: 'Base64', inputEncoding: 'String' }, (output) => {
        resolve(output);
      });
    });
  }

  /**
   * syncronously decompress local storage data
   * @param {string} data
   * @returns {string}
   */
  _decompressLocalStorageData(data) {
    return LZUTF8.decompress(data, { inputEncoding: 'Base64', outputEncoding: 'String' });
  }

  /**
   * clear all save data
   */
  async clearAllData(clearLegacyCC2Data = false) {
    // attempt to delete legacy CC2 save data
    try {
      if (clearLegacyCC2Data && this.isCC2Data) this._cc2LegacyDataHandler.deleteLegacyCC2Data();
    } catch (error) {
      console.error(error);
    }
    // delete server data
    try {
      const userData = await GBC_getUserData();
      // only clear all keys if clearing legacy data to. This should be changed back later to all keys.
      if (clearLegacyCC2Data) Object.keys(userData).forEach((key) => { userData[key] = null; }); // delete ALL COOK + OMT keys
      else OMT_STORAGE_KEYS.forEach((key) => { userData[key] = null; }); // delete only OMT keys
      this._gbUserData = userData;
      await GBC_setUserData(userData);
    } catch (error) {
      OMT_Utils.stylizedLog('OMT_UserData: could not clear data on the game-backend', '#FFFF00');
    }

    // delete local data
    try {
      const storageKey = OMT_Crypt.md5Hash(`${G.BuildEnvironment.APP_ID}-${OMT.envData.settings.user.userId}`);
      // localStorage.removeItem(storageKey);
      window.sdkHandler.trigger('save', {
        key: storageKey,
        value: "",
        callback: () => { }
      });
    } catch (error) {
      OMT_Utils.stylizedLog('OMT_UserData: could not clear local storage data', '#FFFF00');
    }
  }
}
